// src/pages/seeker/SeekerPersonal.jsx
// Personal Details — auto-saves each field on blur / select change

import React, { useEffect, useState, useRef } from 'react';
import { seekerAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { Camera, Star, Phone, Mail, Link, Loader } from 'lucide-react';
import { PhoneCapture, parsePhoneStr, joinPhoneStr } from '../../components/shared/PhoneInput';
import { useConfirm, AddBtn, DelBtn } from '../../components/shared/UI';

const C = {
  navy: '#0e4e95', blue: '#3D80CC', white: '#ffffff',
  offwhite: '#f4f7fb', text: '#1a1a1a', muted: '#666',
  border: '#d0daea', gold: '#e6a817',
};

const SectionTitle = ({ children }) => (
  <div style={{ fontSize: '14px', fontWeight: 700, color: '#E8EDF2', background: '#4A6FA5', padding: '8px 12px', borderRadius: '3px', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
    {children}
  </div>
);

const FL = ({ label, children, hint, half }) => (
  <div style={{ marginBottom: '14px', width: half ? 'calc(50% - 6px)' : '100%' }}>
    <label className="form-label">{label}</label>
    {children}
    {hint && <div style={{ fontSize: '11px', color: 'var(--secondary)', marginTop: '3px', fontWeight: 600 }}>{hint}</div>}
  </div>
);

const FI = ({ value, onChange, onBlur, type = 'text', placeholder, maxLength, rows }) => {
  if (rows) return <textarea value={value || ''} onChange={e => onChange(e.target.value)} onBlur={onBlur} placeholder={placeholder} maxLength={maxLength} rows={rows} className="form-input" style={{ resize: 'vertical' }} />;
  return <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} onBlur={onBlur} placeholder={placeholder} maxLength={maxLength} className="form-input" />;
};

const FS = ({ value, onChange, options, placeholder = '— Select —' }) => (
  <select value={value || ''} onChange={e => onChange(e.target.value || '')} className="form-input" style={{ background: 'white' }}>
    <option value="">{placeholder}</option>
    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

// ── Contact row ────────────────────────────────────────
const ContactRow = ({ contact, onUpdate, onDelete, onSetPrimary }) => {
  const [editing,    setEditing]    = useState(false);
  const [value,      setValue]      = useState(contact.value);
  const [label,      setLabel]      = useState(contact.label || '');
  const [saving,     setSaving]     = useState(false);
  const [phoneCode,  setPhoneCode]  = useState(() => parsePhoneStr(contact.value).code || '+61');
  const [phoneLocal, setPhoneLocal] = useState(() => parsePhoneStr(contact.value).number);

  const save = async () => {
    setSaving(true);
    const finalValue = contact.type === 'PHONE' ? joinPhoneStr(phoneCode, phoneLocal) : value;
    try { await onUpdate(contact.id, { value: finalValue, label: label.trim() || null }); setEditing(false); toast.success('Contact updated'); }
    catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };
  const cancel = () => {
    setValue(contact.value);
    setLabel(contact.label || '');
    setPhoneCode(parsePhoneStr(contact.value).code || '+61');
    setPhoneLocal(parsePhoneStr(contact.value).number);
    setEditing(false);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', background: contact.isPrimary ? '#f0f6ff' : C.offwhite, border: `1px solid ${contact.isPrimary ? C.blue : C.border}`, borderRadius: '4px', marginBottom: '8px' }}>
      <span style={{ color: contact.type === 'PHONE' ? C.navy : contact.type === 'EMAIL' ? C.blue : C.muted, flexShrink: 0 }}>
        {contact.type === 'PHONE' ? <Phone size={14} /> : contact.type === 'EMAIL' ? <Mail size={14} /> : <Link size={14} />}
      </span>
      {editing ? (
        <div style={{ flex: 1 }}>
          {contact.type === 'PHONE' ? (
            <>
              <PhoneCapture
                code={phoneCode} number={phoneLocal} label={label}
                onCodeChange={setPhoneCode}
                onNumberChange={setPhoneLocal}
                onLabelChange={setLabel}
              />
              <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', marginTop: '6px' }}>
                <button onClick={save} disabled={saving} className="btn btn-success btn-sm">{saving ? '…' : '✓ Save'}</button>
                <button onClick={cancel} className="btn btn-danger btn-sm">✕</button>
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <input value={value} onChange={e => setValue(e.target.value)} className="form-input" style={{ flex: 2, minWidth: '140px' }} />
              <input value={label} onChange={e => setLabel(e.target.value)} placeholder="Label" maxLength={50} className="form-input" style={{ flex: 1, minWidth: '100px' }} />
              <button onClick={save} disabled={saving} className="btn btn-success btn-sm">{saving ? '…' : '✓ Save'}</button>
              <button onClick={cancel} className="btn btn-danger btn-sm">✕</button>
            </div>
          )}
        </div>
      ) : (
        <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => setEditing(true)}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: C.text }}>{contact.value}</div>
          {contact.label && <div style={{ fontSize: '11px', color: C.muted }}>{contact.label}</div>}
        </div>
      )}
      <button onClick={() => onSetPrimary(contact.id)} title={contact.isPrimary ? 'Primary' : 'Set as primary'}
        style={{ background: 'none', border: 'none', cursor: contact.isPrimary ? 'default' : 'pointer', color: contact.isPrimary ? C.gold : '#ccc', padding: '2px', flexShrink: 0 }}>
        <Star size={15} fill={contact.isPrimary ? C.gold : 'none'} />
      </button>
      <DelBtn onClick={() => onDelete(contact.id)} size={14} />
    </div>
  );
};

const validateEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const validatePhone = (v) => /^[\d\s\-().]{4,20}$/.test(v);

// ── Add contact row ────────────────────────────────────
const AddContactRow = ({ type, onAdd }) => {
  const [open,   setOpen]   = useState(false);
  const [value,  setValue]  = useState('');
  const [label,  setLabel]  = useState('');
  const [prefix, setPrefix] = useState('+61');
  const [error,  setError]  = useState('');
  const [saving, setSaving] = useState(false);

  const validate = (v) => {
    if (type === 'EMAIL' && v && !validateEmail(v)) return 'Enter a valid email address';
    if (type === 'PHONE' && v && !validatePhone(v)) return 'Enter digits only (e.g. 412 345 678)';
    return '';
  };

  const fullValue = type === 'PHONE' ? `${prefix} ${value}`.trim() : value;

  const save = async () => {
    const err = validate(value);
    if (err) { setError(err); return; }
    if (!value.trim()) return;
    setSaving(true);
    try {
      await onAdd({ type, value: fullValue.trim(), label: label.trim() || null, isPrimary: false });
      setValue(''); setLabel(''); setOpen(false); setError('');
    } catch { toast.error('Failed to add'); }
    finally { setSaving(false); }
  };

  if (!open) return (
    <AddBtn onClick={() => setOpen(true)} size={12} title={`Add ${type === 'PHONE' ? 'Phone' : type === 'EMAIL' ? 'Email' : 'Link'}`} />
  );

  return (
    <div style={{ padding: '8px 10px', background: C.offwhite, border: `1px dashed ${C.border}`, borderRadius: '4px', marginBottom: '8px' }}>
      {type === 'PHONE' ? (
        <>
          <PhoneCapture
            code={prefix} number={value} label={label}
            onCodeChange={setPrefix}
            onNumberChange={v => { setValue(v); setError(validate(v)); }}
            onLabelChange={setLabel}
          />
          <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', marginTop: '8px' }}>
            <button onClick={save} disabled={saving || !value.trim()} className="btn btn-success btn-sm">
              {saving ? '…' : 'Add'}
            </button>
            <button onClick={() => { setOpen(false); setPrefix('+61'); setValue(''); setLabel(''); setError(''); }} className="btn btn-danger btn-sm">✕</button>
          </div>
          {error && <div style={{ fontSize: '11px', color: 'var(--danger)', marginTop: '4px' }}>{error}</div>}
        </>
      ) : (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input value={value}
            onChange={e => { setValue(e.target.value); setError(validate(e.target.value)); }}
            onKeyDown={e => e.key === 'Enter' && save()}
            placeholder={type === 'EMAIL' ? 'Email address' : 'https://…'}
            className="form-input" style={{ flex: 2, minWidth: '140px', borderColor: error ? 'var(--danger)' : undefined }}
            autoFocus />
          <input value={label} onChange={e => setLabel(e.target.value)} placeholder="Label (e.g. Personal, Work)" maxLength={50}
            className="form-input" style={{ flex: 1, minWidth: '100px' }} />
          <button onClick={save} disabled={saving || !value.trim()} className="btn btn-success btn-sm">
            {saving ? '…' : 'Add'}
          </button>
          <button onClick={() => { setOpen(false); setValue(''); setLabel(''); setError(''); }} className="btn btn-danger btn-sm">✕</button>
          {error && <div style={{ fontSize: '11px', color: 'var(--danger)', marginTop: '4px', width: '100%' }}>{error}</div>}
        </div>
      )}
    </div>
  );
};

// ── DOB formatter: dd-mmm-yyyy ─────────────────────────
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const fmtDOB = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return `${String(d.getUTCDate()).padStart(2,'0')}-${MONTHS[d.getUTCMonth()]}-${d.getUTCFullYear()}`;
};

// ── Date of Birth input: shows dd-mmm-yyyy, calendar icon opens native picker ──
const DoBInput = ({ value, onChange }) => {
  const pickerRef = useRef();
  // Display text: if we have an ISO date (yyyy-mm-dd), show formatted; else show what the user typed
  const display = value ? fmtDOB(value) : '';

  return (
    <div style={{ display: 'flex', gap: '0', alignItems: 'stretch' }}>
      {/* Read-only formatted display */}
      <input
        type="text"
        readOnly
        value={display}
        placeholder="dd-mmm-yyyy"
        className="form-input"
        style={{ borderRadius: '3px 0 0 3px', flex: 1, cursor: 'default' }}
      />
      {/* Calendar button — clicking opens the hidden date picker */}
      <button
        type="button"
        onClick={() => pickerRef.current?.showPicker?.() || pickerRef.current?.click()}
        style={{ padding: '0 10px', background: '#4A6FA5', border: `1px solid #4A6FA5`, borderLeft: 'none', borderRadius: '0 3px 3px 0', cursor: 'pointer', display: 'flex', alignItems: 'center', color: 'white', flexShrink: 0 }}
        title="Pick a date"
      >
        📅
      </button>
      {/* Hidden native date picker — triggered by button */}
      <input
        ref={pickerRef}
        type="date"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
        tabIndex={-1}
      />
    </div>
  );
};


// ══════════════════════════════════════════════════════
export const SeekerPersonal = () => {
  const { confirm, dialog } = useConfirm();
  const photoRef = useRef();

  const [profile,     setProfile]     = useState(null);
  const [contacts,    setContacts]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [photoSaving, setPhotoSaving] = useState(false);

  const [form, setForm] = useState({
    firstName: '', lastName: '', headline: '',
    city: '', country: '', residentialAddress: '',
    gender: '', dateOfBirth: '',
    nationality: '', linkedinUrl: '', portfolioUrl: '',
    workRightStatus: '', availabilityStatus: '', workPreference: '',
  });

  const setF = (field) => (val) => setForm(f => ({ ...f, [field]: val }));

  const saveField = async (field, val) => {
    try {
      await seekerAPI.updateProfile({ [field]: val === '' ? null : val });
      toast.success('Saved', { duration: 1200, id: 'autosave' });
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Save failed');
    }
  };

  const blurSave   = (field) => (e) => saveField(field, e.target.value);
  const selectSave = (field) => (val) => { setF(field)(val); saveField(field, val); };

  const loadAll = async () => {
    try {
      const [profRes, contRes] = await Promise.all([seekerAPI.getProfile(), seekerAPI.getContacts()]);
      const p = profRes.data.data;
      setProfile(p);
      setContacts(contRes.data.data || []);
      setForm({
        firstName:          p.firstName          || '',
        lastName:           p.lastName           || '',
        headline:           p.headline           || '',
        city:               p.city               || '',
        country:            p.country            || '',
        residentialAddress: p.residentialAddress || '',
        gender:             p.gender             || '',
        dateOfBirth:        p.dateOfBirth ? p.dateOfBirth.slice(0, 10) : '',
        nationality:        p.nationality        || '',
        linkedinUrl:        p.linkedinUrl        || '',
        portfolioUrl:       p.portfolioUrl       || '',
        workRightStatus:    p.workRightStatus    || '',
        availabilityStatus: p.availabilityStatus || '',
        workPreference:     p.workPreference     || '',
      });
    } catch { toast.error('Failed to load profile'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData(); fd.append('photo', file);
    setPhotoSaving(true);
    try {
      await seekerAPI.uploadProfilePhoto(fd);
      await loadAll();
      toast.success('Photo updated');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Photo upload failed');
    } finally { setPhotoSaving(false); e.target.value = ''; }
  };

  const handleAddContact    = async (data) => { await seekerAPI.addContact(data); await loadAll(); };
  const handleUpdateContact = async (id, data) => { await seekerAPI.updateContact(id, data); await loadAll(); };
  const handleDeleteContact = async (id) => {
    let affectedCVs = [];
    try {
      const res = await seekerAPI.getCVProfiles();
      const cvs = res.data.data || [];
      affectedCVs = cvs.filter(cv => (cv.sectionConfig?.cvContacts || []).includes(id));
    } catch { /* ignore */ }

    if (affectedCVs.length > 0) {
      const cvList = affectedCVs.map(cv => `"${cv.title}"`).join(', ');
      if (!await confirm('Delete Contact', `This contact is included in ${affectedCVs.length === 1 ? 'a CV' : 'CVs'}: ${cvList}\n\nDeleting it will remove it from all of the above. Continue?`)) return;
      affectedCVs.forEach(cv => {
        const cfg = cv.sectionConfig || {};
        seekerAPI.updateCVProfile(cv.id, { sectionConfig: { ...cfg, cvContacts: (cfg.cvContacts || []).filter(cid => cid !== id) } }).catch(() => {});
      });
    } else {
      if (!await confirm('Delete Contact', 'Are you sure you want to delete this contact?')) return;
    }
    await seekerAPI.deleteContact(id);
    await loadAll();
  };

  const handleSetPrimary = async (id) => { await seekerAPI.updateContact(id, { isPrimary: true }); await loadAll(); };

  const phones = contacts.filter(c => c.type === 'PHONE');
  const emails = contacts.filter(c => c.type === 'EMAIL');
  const links  = contacts.filter(c => c.type === 'URL');

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: C.muted }}>Loading…</div>;

  return (
    <div className="fade-in">
      {dialog}
      <div className="page-header">
        <h1>Personal Details</h1>
        <p>Changes are saved automatically when you leave each field</p>
      </div>

      <div className="page-content">

        {/* ── Photo ── */}
        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '20px 24px', marginBottom: '16px' }}>
          <SectionTitle>Profile Photo</SectionTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div onClick={() => photoRef.current.click()}
              style={{ width: 90, height: 90, borderRadius: '50%', background: profile?.photoUrl ? 'transparent' : C.offwhite, border: `2px solid ${C.border}`, overflow: 'hidden', cursor: 'pointer', position: 'relative', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {profile?.photoUrl
                ? <img src={profile.photoUrl} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <span style={{ fontSize: 40, opacity: 0.3 }}>👤</span>
              }
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.opacity = 1} onMouseLeave={e => e.currentTarget.style.opacity = 0}>
                <Camera size={22} color="white" />
              </div>
              {photoSaving && (
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Loader size={20} color={C.navy} />
                </div>
              )}
            </div>
            <input ref={photoRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={handlePhotoChange} />
            <div>
              <button onClick={() => photoRef.current.click()} className="btn btn-primary">
                {profile?.photoUrl ? 'Change Photo' : 'Upload Photo'}
              </button>
              <p style={{ fontSize: '11px', color: C.muted, margin: '6px 0 0' }}>JPEG, PNG or WebP · max 5MB</p>
            </div>
          </div>
        </div>

        {/* ── Personal Information ── */}
        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '20px 24px', marginBottom: '16px' }}>
          <SectionTitle>Personal Information</SectionTitle>
          <div className="form-row">
            <FL label="First Name" half>
              <FI value={form.firstName} onChange={setF('firstName')} onBlur={blurSave('firstName')} placeholder="First name" />
            </FL>
            <FL label="Last Name" half>
              <FI value={form.lastName} onChange={setF('lastName')} onBlur={blurSave('lastName')} placeholder="Last name" />
            </FL>
          </div>
          <FL label="Professional Headline" hint="e.g. Senior Accountant · max 120 characters">
            <FI value={form.headline} onChange={setF('headline')} onBlur={blurSave('headline')} placeholder="Your professional headline" maxLength={120} />
          </FL>
          <div className="form-row" style={{ marginTop: '4px' }}>
            <FL label="Gender" half>
              <FS value={form.gender} onChange={selectSave('gender')} options={[
                { value: 'MALE', label: 'Male' }, { value: 'FEMALE', label: 'Female' },
                { value: 'ALTERNATIVE', label: 'Alternative' }, { value: 'PREFER_NOT_TO_SAY', label: 'Prefer not to say' },
              ]} />
            </FL>
            <FL label="Date of Birth" half>
              <DoBInput value={form.dateOfBirth} onChange={val => { setF('dateOfBirth')(val); saveField('dateOfBirth', val); }} />
            </FL>
            <FL label="Nationality" half>
              <FI value={form.nationality} onChange={setF('nationality')} onBlur={blurSave('nationality')} placeholder="e.g. Australian" maxLength={100} />
            </FL>
            <FL label="Work Right Status" half>
              <FS value={form.workRightStatus} onChange={selectSave('workRightStatus')} options={[
                { value: 'CITIZEN', label: 'Citizen' },
                { value: 'NON_CITIZEN', label: 'Non-citizen' },
              ]} />
            </FL>
          </div>
        </div>

        {/* ── Contacts ── */}
        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '20px 24px', marginBottom: '16px' }}>
          <SectionTitle>Contacts</SectionTitle>
          <p style={{ fontSize: '12px', color: C.muted, margin: '0 0 14px' }}>Click any contact to edit it. ★ marks the primary — starred contacts are included in new CVs by default.</p>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: C.navy, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '5px' }}><Phone size={13} /> Phone Numbers</div>
            {phones.map(c => <ContactRow key={c.id} contact={c} onUpdate={handleUpdateContact} onDelete={handleDeleteContact} onSetPrimary={handleSetPrimary} />)}
            <AddContactRow type="PHONE" onAdd={handleAddContact} />
          </div>
          <div>
            <div style={{ fontSize: '12px', fontWeight: 700, color: C.navy, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '5px' }}><Mail size={13} /> Email Addresses</div>
            {emails.map(c => <ContactRow key={c.id} contact={c} onUpdate={handleUpdateContact} onDelete={handleDeleteContact} onSetPrimary={handleSetPrimary} />)}
            <AddContactRow type="EMAIL" onAdd={handleAddContact} />
          </div>
        </div>

        {/* ── Links ── */}
        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '20px 24px', marginBottom: '16px' }}>
          <SectionTitle>Links</SectionTitle>
          <p style={{ fontSize: '12px', color: C.muted, margin: '0 0 14px' }}>Click any link to edit it.</p>
          {links.map(c => <ContactRow key={c.id} contact={c} onUpdate={handleUpdateContact} onDelete={handleDeleteContact} onSetPrimary={handleSetPrimary} />)}
          <AddContactRow type="URL" onAdd={handleAddContact} />
        </div>

        {/* ── Residence ── */}
        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '20px 24px', marginBottom: '40px' }}>
          <SectionTitle>Residence</SectionTitle>
          <div className="form-row">
            <FL label="City" half>
              <FI value={form.city} onChange={setF('city')} onBlur={blurSave('city')} placeholder="e.g. Sydney" />
            </FL>
            <FL label="Country" half>
              <input list="country-list" value={form.country || ''}
                onChange={e => {
                  const val = e.target.value;
                  setF('country')(val);
                  // Save immediately when a datalist suggestion is selected (full match)
                  const known = ['Afghanistan','Albania','Algeria','Andorra','Angola','Antigua and Barbuda','Argentina','Armenia','Australia','Austria','Azerbaijan','Bahamas','Bahrain','Bangladesh','Barbados','Belarus','Belgium','Belize','Benin','Bhutan','Bolivia','Bosnia and Herzegovina','Botswana','Brazil','Brunei','Bulgaria','Burkina Faso','Burundi','Cabo Verde','Cambodia','Cameroon','Canada','Central African Republic','Chad','Chile','China','Colombia','Comoros','Congo','Costa Rica','Croatia','Cuba','Cyprus','Czech Republic','Denmark','Djibouti','Dominica','Dominican Republic','Ecuador','Egypt','El Salvador','Equatorial Guinea','Eritrea','Estonia','Eswatini','Ethiopia','Fiji','Finland','France','Gabon','Gambia','Georgia','Germany','Ghana','Greece','Grenada','Guatemala','Guinea','Guinea-Bissau','Guyana','Haiti','Honduras','Hungary','Iceland','India','Indonesia','Iran','Iraq','Ireland','Israel','Italy','Jamaica','Japan','Jordan','Kazakhstan','Kenya','Kiribati','Kuwait','Kyrgyzstan','Laos','Latvia','Lebanon','Lesotho','Liberia','Libya','Liechtenstein','Lithuania','Luxembourg','Madagascar','Malawi','Malaysia','Maldives','Mali','Malta','Marshall Islands','Mauritania','Mauritius','Mexico','Micronesia','Moldova','Monaco','Mongolia','Montenegro','Morocco','Mozambique','Myanmar','Namibia','Nauru','Nepal','Netherlands','New Zealand','Nicaragua','Niger','Nigeria','North Korea','North Macedonia','Norway','Oman','Pakistan','Palau','Palestine','Panama','Papua New Guinea','Paraguay','Peru','Philippines','Poland','Portugal','Qatar','Romania','Russia','Rwanda','Saint Kitts and Nevis','Saint Lucia','Saint Vincent and the Grenadines','Samoa','San Marino','Sao Tome and Principe','Saudi Arabia','Senegal','Serbia','Seychelles','Sierra Leone','Singapore','Slovakia','Slovenia','Solomon Islands','Somalia','South Africa','South Korea','South Sudan','Spain','Sri Lanka','Sudan','Suriname','Sweden','Switzerland','Syria','Taiwan','Tajikistan','Tanzania','Thailand','Timor-Leste','Togo','Tonga','Trinidad and Tobago','Tunisia','Turkey','Turkmenistan','Tuvalu','Uganda','Ukraine','United Arab Emirates','United Kingdom','United States','Uruguay','Uzbekistan','Vanuatu','Vatican City','Venezuela','Vietnam','Yemen','Zambia','Zimbabwe'];
                  if (known.includes(val)) saveField('country', val);
                }}
                onBlur={blurSave('country')}
                placeholder="e.g. Australia" maxLength={100} className="form-input" />
              <datalist id="country-list">
                {['Afghanistan','Albania','Algeria','Andorra','Angola','Antigua and Barbuda','Argentina','Armenia','Australia','Austria','Azerbaijan','Bahamas','Bahrain','Bangladesh','Barbados','Belarus','Belgium','Belize','Benin','Bhutan','Bolivia','Bosnia and Herzegovina','Botswana','Brazil','Brunei','Bulgaria','Burkina Faso','Burundi','Cabo Verde','Cambodia','Cameroon','Canada','Central African Republic','Chad','Chile','China','Colombia','Comoros','Congo','Costa Rica','Croatia','Cuba','Cyprus','Czech Republic','Denmark','Djibouti','Dominica','Dominican Republic','Ecuador','Egypt','El Salvador','Equatorial Guinea','Eritrea','Estonia','Eswatini','Ethiopia','Fiji','Finland','France','Gabon','Gambia','Georgia','Germany','Ghana','Greece','Grenada','Guatemala','Guinea','Guinea-Bissau','Guyana','Haiti','Honduras','Hungary','Iceland','India','Indonesia','Iran','Iraq','Ireland','Israel','Italy','Jamaica','Japan','Jordan','Kazakhstan','Kenya','Kiribati','Kuwait','Kyrgyzstan','Laos','Latvia','Lebanon','Lesotho','Liberia','Libya','Liechtenstein','Lithuania','Luxembourg','Madagascar','Malawi','Malaysia','Maldives','Mali','Malta','Marshall Islands','Mauritania','Mauritius','Mexico','Micronesia','Moldova','Monaco','Mongolia','Montenegro','Morocco','Mozambique','Myanmar','Namibia','Nauru','Nepal','Netherlands','New Zealand','Nicaragua','Niger','Nigeria','North Korea','North Macedonia','Norway','Oman','Pakistan','Palau','Palestine','Panama','Papua New Guinea','Paraguay','Peru','Philippines','Poland','Portugal','Qatar','Romania','Russia','Rwanda','Saint Kitts and Nevis','Saint Lucia','Saint Vincent and the Grenadines','Samoa','San Marino','Sao Tome and Principe','Saudi Arabia','Senegal','Serbia','Seychelles','Sierra Leone','Singapore','Slovakia','Slovenia','Solomon Islands','Somalia','South Africa','South Korea','South Sudan','Spain','Sri Lanka','Sudan','Suriname','Sweden','Switzerland','Syria','Taiwan','Tajikistan','Tanzania','Thailand','Timor-Leste','Togo','Tonga','Trinidad and Tobago','Tunisia','Turkey','Turkmenistan','Tuvalu','Uganda','Ukraine','United Arab Emirates','United Kingdom','United States','Uruguay','Uzbekistan','Vanuatu','Vatican City','Venezuela','Vietnam','Yemen','Zambia','Zimbabwe'].map(c => <option key={c} value={c} />)}
              </datalist>
            </FL>
          </div>
          <FL label="Street Address">
            <FI value={form.residentialAddress} onChange={setF('residentialAddress')} onBlur={blurSave('residentialAddress')} placeholder="e.g. house # and street name" maxLength={300} />
          </FL>
        </div>

      </div>
    </div>
  );
};
